/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Form, Row, Col, Button } from "react-bootstrap";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { getDateLocaleConfig } from "i18n/locale";

import ReactSelect from "components/base/ReactSelect";
import { components } from "react-select";

import {
    getRecordById,
    setMaintenanceDocument,
    setDescription,
    setNodes,
    setGroups,
    setStarted,
    setFinished,
    updateRecord,
    updateRecordGroupEquipment,
    updateRecordGroup,
    updateRecordWithGroups,
} from "./features/inspectionRecordSlice";

import PhoenixModal from "../components/PhoenixModal";
import { InspectionRouteTree } from "../components/InspectionRouteTree/InspectionRouteTree";
import {
    EquipmentInspectionStatus,
    EquipmentInspectionStatusLabel,
} from "./utils/enumEquipmentInspectionStatus";
import { applyNodeStatusesToRecordGroups } from "../InspectionRoute/utils/functionsTreeNode";
import { IInspectionRecordGroup } from "../model/inspection-record-group.model";
import { markAllEquipmentsAsInspected } from "./utils/treeUtils";
import { handleRenderNodeExtras, rowStyle } from "./treeRules";
import { ConditionType } from "../model/enumerations/condition-type.model";

type InspectionRecordFormProps = {
    mode?: "view" | "edit";
};

type StatusKey = EquipmentInspectionStatus;
type CondKey = ConditionType;

type CondOption = { value: CondKey; label: string; isDisabled?: boolean };
type StatusOption = { value: StatusKey; label: string; isDisabled?: boolean };

/* ============================
   helpers URL
============================ */
function setsEqual<T>(a: Set<T>, b: Set<T>) {
    if (a.size !== b.size) return false;
    for (const v of a) if (!b.has(v)) return false;
    return true;
}

function serializeSorted<T>(set: Set<T>) {
    return Array.from(set)
        .map((x) => String(x))
        .sort((a, b) => a.localeCompare(b))
        .join(",");
}

function parseCsvParam(param: string | null): string[] {
    if (!param) return [];
    return param
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
}

/* ============================
   parsers cond/status
============================ */
function toConditionType(v: any): CondKey | null {
    if (!v) return null;
    if (Object.values(ConditionType).includes(v)) return v as CondKey;

    const s = String(v).trim();
    if (Object.values(ConditionType).includes(s as any)) return s as CondKey;

    const byLabel: Record<string, CondKey> = {
        Normal: ConditionType.NORMAL,
        "Baixo Risco": ConditionType.LOW_RISK,
        "Médio Risco": ConditionType.MEDIUM_RISK,
        "Alto Risco": ConditionType.HIGH_RISK,
        "Alto Risco Iminente": ConditionType.IMMINENT_HIGH_RISK,
    };

    if (byLabel[s]) return byLabel[s];

    const upper = s.toUpperCase();
    if (upper === "MEDIO RISCO" || upper === "MÉDIO RISCO") return ConditionType.MEDIUM_RISK;
    if (
        upper === "IMMINENT HIGH RISK" ||
        upper === "IMINENTE" ||
        upper === "ALTO RISCO IMINENTE"
    )
        return ConditionType.IMMINENT_HIGH_RISK;

    return null;
}

function getNodeConditionType(node: any): CondKey | null {
    const direct =
        node?.conditionType ??
        node?.condition ??
        node?.status?.conditionType ??
        node?.status?.condition ??
        node?.anomaly?.conditionType ??
        node?.anomaly?.condition ??
        null;

    return toConditionType(direct);
}

function toInspectionStatus(v: any): StatusKey | null {
    if (!v) return null;

    if (Object.values(EquipmentInspectionStatus).includes(v)) return v as StatusKey;

    const s = String(v).trim();
    if (Object.values(EquipmentInspectionStatus).includes(s as any)) return s as StatusKey;

    const reverse: Record<string, StatusKey> = Object.entries(EquipmentInspectionStatusLabel as any).reduce(
        (acc: any, [k, label]) => {
            acc[String(label).toLowerCase()] = k as StatusKey;
            return acc;
        },
        {}
    );

    const byLabel = reverse[s.toLowerCase()];
    if (byLabel) return byLabel;

    return null;
}

/** Tenta achar o status no node */
function getNodeInspectionStatus(node: any): StatusKey | null {
    const direct =
        node?.status ??
        node?.inspectionStatus ??
        node?.equipmentStatus ??
        node?.status?.inspectionStatus ??
        node?.status?.equipmentStatus ??
        null;

    return toInspectionStatus(direct);
}

function getEffectiveStatus(node: any, inherited: StatusKey | null): StatusKey | null {
    const own = getNodeInspectionStatus(node);
    return own ?? inherited;
}

/**
 * Conta condições considerando o filtro de status no equipamento ancestral.
 */
function countConditionsScopedToEquipmentStatus(
    roots: any[] = [],
    selectedStatuses: Set<StatusKey>
): Record<CondKey, number> {
    const counts = {
        [ConditionType.NORMAL]: 0,
        [ConditionType.LOW_RISK]: 0,
        [ConditionType.MEDIUM_RISK]: 0,
        [ConditionType.HIGH_RISK]: 0,
        [ConditionType.IMMINENT_HIGH_RISK]: 0,
    } as Record<CondKey, number>;

    const hasStatusFilter = selectedStatuses.size > 0;

    const walk = (n: any, equipStatus: StatusKey | null) => {
        const effStatus = getEffectiveStatus(n, equipStatus);

        const cond = getNodeConditionType(n);
        if (cond) {
            const passStatus = !hasStatusFilter ? true : !!effStatus && selectedStatuses.has(effStatus);
            if (passStatus) counts[cond] += 1;
        }

        (n?.children ?? []).forEach((ch: any) => walk(ch, effStatus));
    };

    roots.forEach((r) => walk(r, null));
    return counts;
}

/**
 * Conta status (equipamentos) considerando o filtro de condição nos itens.
 */
function countEquipmentStatusesScopedToConditions(
    roots: any[] = [],
    selectedConds: Set<CondKey>
): Record<StatusKey, number> {
    const counts = {
        [EquipmentInspectionStatus.INSPECTED]: 0,
        [EquipmentInspectionStatus.NOT_INSPECTED]: 0,
        [EquipmentInspectionStatus.OUT_OF_OPERATION]: 0,
        [EquipmentInspectionStatus.INACCESSIBLE]: 0,
        [EquipmentInspectionStatus.OTHER]: 0,
    } as Record<StatusKey, number>;

    const hasCondFilter = selectedConds.size > 0;

    const walk = (n: any, equipStatus: StatusKey | null): boolean => {
        const effStatus = getEffectiveStatus(n, equipStatus);

        let subtreeHasSelectedCond = false;

        const cond = getNodeConditionType(n);
        if (cond && selectedConds.has(cond)) subtreeHasSelectedCond = true;

        for (const ch of n?.children ?? []) {
            if (walk(ch, effStatus)) subtreeHasSelectedCond = true;
        }

        const ownStatus = getNodeInspectionStatus(n);
        if (ownStatus) {
            const pass = !hasCondFilter ? true : subtreeHasSelectedCond;
            if (pass) counts[ownStatus] += 1;
            return false;
        }

        return subtreeHasSelectedCond;
    };

    roots.forEach((r) => walk(r, null));
    return counts;
}

function buildVisibilityMapWithEquipmentContext(
    roots: any[] = [],
    selectedConds: Set<CondKey>,
    selectedStatuses: Set<StatusKey>
) {
    const vis = new Map<string, boolean>();

    const hasCondFilter = selectedConds.size > 0;
    const hasStatusFilter = selectedStatuses.size > 0;

    const post = (n: any, equipStatus: StatusKey | null): boolean => {
        const effStatus = getEffectiveStatus(n, equipStatus);

        const children = n?.children ?? [];
        const anyChildVisible = children.map((ch: any) => post(ch, effStatus)).some(Boolean);

        const passStatus = !hasStatusFilter ? true : !!effStatus && selectedStatuses.has(effStatus);

        const cond = getNodeConditionType(n);
        const passCond = !hasCondFilter ? true : !!cond && selectedConds.has(cond);

        const isEquip = !!getNodeInspectionStatus(n);

        let selfVisible = false;

        if (!passStatus) {
            selfVisible = false;
        } else if (!hasCondFilter) {
            selfVisible = true;
        } else {
            if (cond) selfVisible = passCond;
            else selfVisible = anyChildVisible;
        }

        const result = selfVisible || anyChildVisible;

        if (n?.id) vis.set(String(n.id), result);

        if (isEquip) return result;
        return result;
    };

    roots.forEach((r) => post(r, null));
    return vis;
}

export default function InspectionRecordForm({ mode = "edit" }: InspectionRecordFormProps) {
    const dispatch: any = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: "translation", ...options });
    const dateLocale = getDateLocaleConfig(i18n.language);
    const dateTimeFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(dateLocale.intlLocale, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            }),
        [dateLocale.intlLocale]
    );

    const [searchParams, setSearchParams] = useSearchParams();
    const searchKey = searchParams.toString(); // chave estável p/ memos

    const isViewMode = mode === "view";
    const { data: record, nodes, groups } = useSelector((state: any) => state.inspectionRecord);

    const isInteractionDisabled = isViewMode || !record?.started;
    const hasStarted = !!record?.started;
    const hasFinished = !!record?.finished;

    const [showFinishModal, setShowFinishModal] = useState(false);

    /* ============================================================
      filtros vêm DA URL
    ============================================================ */
    const selectedStatuses = useMemo(() => {
        const next = new Set<StatusKey>();
        for (const raw of parseCsvParam(searchParams.get("status"))) {
            const st = toInspectionStatus(raw);
            if (st) next.add(st);
        }
        return next;
    }, [searchKey]);

    const selectedConds = useMemo(() => {
        const next = new Set<CondKey>();
        for (const raw of parseCsvParam(searchParams.get("cond"))) {
            const c = toConditionType(raw);
            if (c) next.add(c);
        }
        return next;
    }, [searchKey]);

    const hasAnyFilter = selectedConds.size > 0 || selectedStatuses.size > 0;

    // Contadores dependem dos filtros atuais (URL)
    const conditionCounts = useMemo(() => {
        return countConditionsScopedToEquipmentStatus(nodes ?? [], selectedStatuses);
    }, [nodes, selectedStatuses]);

    const statusCounts = useMemo(() => {
        return countEquipmentStatusesScopedToConditions(nodes ?? [], selectedConds);
    }, [nodes, selectedConds]);

    const condOptions: CondOption[] = useMemo(() => {
        const all = Object.values(ConditionType) as CondKey[];
        const existing = all
            .filter((c) => (conditionCounts?.[c] ?? 0) > 0)
            .sort((a, b) => (conditionCounts[b] ?? 0) - (conditionCounts[a] ?? 0));
        const missing = all.filter((c) => (conditionCounts?.[c] ?? 0) === 0);
        const ordered = [...existing, ...missing];
        const conditionLabelByKey: Record<CondKey, string> = {
            [ConditionType.NORMAL]: tt("pages.thermography.conditionType.normal"),
            [ConditionType.LOW_RISK]: tt("pages.thermography.conditionType.lowRisk"),
            [ConditionType.MEDIUM_RISK]: tt("pages.thermography.conditionType.mediumRisk"),
            [ConditionType.HIGH_RISK]: tt("pages.thermography.conditionType.highRisk"),
            [ConditionType.IMMINENT_HIGH_RISK]: tt("pages.thermography.conditionType.imminentHighRisk"),
        };

        return ordered.map((cond) => {
            const count = conditionCounts?.[cond] ?? 0;
            return {
                value: cond,
                label: `${conditionLabelByKey[cond]} (${count})`,
                isDisabled: count === 0,
            };
        });
    }, [conditionCounts, t]);

    const statusOptions: StatusOption[] = useMemo(() => {
        const all = Object.values(EquipmentInspectionStatus) as StatusKey[];
        const existing = all
            .filter((s) => (statusCounts?.[s] ?? 0) > 0)
            .sort((a, b) => (statusCounts[b] ?? 0) - (statusCounts[a] ?? 0));
        const missing = all.filter((s) => (statusCounts?.[s] ?? 0) === 0);
        const ordered = [...existing, ...missing];
        const statusLabelByKey: Record<StatusKey, string> = {
            [EquipmentInspectionStatus.INSPECTED]: tt("pages.thermography.inspectionRecordForm.statusOptions.inspected"),
            [EquipmentInspectionStatus.NOT_INSPECTED]: tt("pages.thermography.inspectionRecordForm.statusOptions.notInspected"),
            [EquipmentInspectionStatus.OUT_OF_OPERATION]: tt("pages.thermography.inspectionRecordForm.statusOptions.outOfOperation"),
            [EquipmentInspectionStatus.INACCESSIBLE]: tt("pages.thermography.inspectionRecordForm.statusOptions.inaccessible"),
            [EquipmentInspectionStatus.OTHER]: tt("pages.thermography.inspectionRecordForm.statusOptions.other"),
        };

        return ordered.map((st) => {
            const count = statusCounts?.[st] ?? 0;
            return {
                value: st,
                label: `${statusLabelByKey[st]} (${count})`,
                isDisabled: count === 0,
            };
        });
    }, [statusCounts, t]);

    const selectedCondOptions = useMemo(
        () => condOptions.filter((o) => selectedConds.has(o.value) && !o.isDisabled),
        [condOptions, selectedConds]
    );
    const selectedStatusOptions = useMemo(
        () => statusOptions.filter((o) => selectedStatuses.has(o.value) && !o.isDisabled),
        [statusOptions, selectedStatuses]
    );

    // Atualiza somente os params cond/status preservando quaisquer outros params.
    const setFilterParams = (nextConds: Set<CondKey>, nextStatuses: Set<StatusKey>) => {
        const next = new URLSearchParams(searchParams);

        if (nextStatuses.size) next.set("status", serializeSorted(nextStatuses));
        else next.delete("status");

        if (nextConds.size) next.set("cond", serializeSorted(nextConds));
        else next.delete("cond");

        setSearchParams(next, { replace: true });
    };

    const onChangeConditions = (value: any) => {
        const arr = (value ?? []) as CondOption[];
        const enabled = new Set(arr.filter((o) => !o.isDisabled).map((o) => o.value));
        setFilterParams(enabled, selectedStatuses);
    };

    const onChangeStatuses = (value: any) => {
        const arr = (value ?? []) as StatusOption[];
        const enabled = new Set(arr.filter((o) => !o.isDisabled).map((o) => o.value));
        setFilterParams(selectedConds, enabled);
    };

    const clearAllFilters = () => {
        setFilterParams(new Set(), new Set());
    };

    useEffect(() => {
        // Aguarda o carregamento real do registro/árvore antes de podar filtros inválidos
        if (!record?.id) return;
        if (!nodes || nodes.length === 0) return;

        const enabledConds = new Set(condOptions.filter((o) => !o.isDisabled).map((o) => o.value));
        const enabledStatuses = new Set(statusOptions.filter((o) => !o.isDisabled).map((o) => o.value));

        const prunedConds = new Set<CondKey>();
        selectedConds.forEach((c) => enabledConds.has(c) && prunedConds.add(c));

        const prunedStatuses = new Set<StatusKey>();
        selectedStatuses.forEach((s) => enabledStatuses.has(s) && prunedStatuses.add(s));

        const changed = !setsEqual(prunedConds, selectedConds) || !setsEqual(prunedStatuses, selectedStatuses);
        if (changed) setFilterParams(prunedConds, prunedStatuses);
    }, [record?.id, nodes, condOptions, statusOptions, searchKey]);

    const visibilityMap = useMemo(() => {
        return buildVisibilityMapWithEquipmentContext(nodes ?? [], selectedConds, selectedStatuses);
    }, [nodes, selectedConds, selectedStatuses]);

    const formatDateTime = (date?: string | null) => {
        if (!date) return "--";
        const parsed = dayjs(date);
        if (!parsed.isValid()) return "--";
        return dateTimeFormatter.format(parsed.toDate());
    };

    const startedByName = record?.startedBy?.name ?? record?.startedBy?.name ?? "--";
    const finishedByName = record?.finishedBy?.name ?? record?.finishedBy?.name ?? "--";

    const groupNames = useMemo(() => {
        const names: string[] = [];
        const walk = (list: any[] = []) => {
            list.forEach((g) => {
                if (g?.name && !g?.finished) names.push(g.name);
            });
        };
        walk(groups);
        return names;
    }, [groups]);

    const areAllGroupsFinished = (groupsArg: IInspectionRecordGroup[] = []): boolean => {
        const allFinished = (list: IInspectionRecordGroup[]): boolean => list.every((group) => !!group.finished);
        return groupsArg.length > 0 && allFinished(groupsArg);
    };

    const handleUpdateOnBlur = () => {
        if (record?.id) dispatch(updateRecord(record.id));
    };

    const handleStartInspection = () => {
        if (isViewMode || record?.started) return;
        dispatch(setStarted());
        if (record?.id) dispatch(updateRecord(record.id));
    };

    const handleStatusChange = (equipmentId: string, status: EquipmentInspectionStatus) => {
        if (isInteractionDisabled) return;
        dispatch(updateRecordGroupEquipment({ id: equipmentId, status }));
    };

    const handleFinishGroup = (groupId: string) => {
        if (isInteractionDisabled) return;
        dispatch(updateRecordGroup(groupId));

        if (areAllGroupsFinished(groups)) {
            dispatch(setFinished());
            dispatch(updateRecordWithGroups(record?.id));
        }
    };

    const handleFinishInspection = () => {
        if (isInteractionDisabled || !record?.id || hasFinished) return;

        const inspectedNodes = nodes.map((n: any) => markAllEquipmentsAsInspected(n));
        dispatch(setNodes(inspectedNodes));

        const updatedGroups = applyNodeStatusesToRecordGroups(inspectedNodes, groups);
        dispatch(setGroups(updatedGroups));

        dispatch(setFinished());
        dispatch(updateRecordWithGroups(record.id));
    };

    const buildReportQuery = () => {
        const params = new URLSearchParams();

        if (selectedStatuses.size) params.set("status", serializeSorted(selectedStatuses));
        if (selectedConds.size) params.set("cond", serializeSorted(selectedConds));

        return params.toString();
    };

    const onPrint = () => {
        const q = buildReportQuery();
        const from = `${location.pathname}${location.search}`;
        navigate(`/inspectionRecords/report/${id}${q ? `?${q}` : ""}`, { state: { from } });

    };

    useEffect(() => {
        if (id) dispatch(getRecordById(id));
    }, [id, dispatch]);

    if (!record) return <div className="p-4">{tt("pages.thermography.inspectionRecordForm.loading")}</div>;

    const ClearIndicator = (props: any) => (
        <components.ClearIndicator {...props}>
            <span
                style={{
                    color: "var(--phoenix-primary, var(--bs-primary))",
                    fontSize: 12,
                    fontWeight: 700,
                    paddingLeft: 5,
                    margin: 0,
                    lineHeight: "16px",
                }}
            >
                {tt("pages.thermography.inspectionRecordForm.filters.clear")}
            </span>
        </components.ClearIndicator>
    );

    const selectStyles = {
        control: (base: any, state: any) => ({
            ...base,
            minHeight: 36,
            height: "auto",
            padding: 2,
            boxShadow: "none",
            borderColor: state.isFocused
                ? "var(--phoenix-primary, var(--bs-primary))"
                : "var(--phoenix-border-color, #dee2e6)",
            "&:hover": {
                borderColor: "var(--phoenix-primary, var(--bs-primary))",
            },
        }),
    };

    return (
        <div
            style={{
                height: "88dvh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}
        >
            {/* HEADER (fixo) */}
            <div className="d-flex justify-content-between align-items-center mb-3" style={{ flex: "0 0 auto" }}>
                <Row className="g-3" style={{ width: "100%" }}>
                    <Col xs={8} className="d-flex justify-content-start">
                        <h5 className="m-0 fw-normal">
                            <span className="fw-bold">{tt("pages.thermography.inspectionRecordForm.header.title")}:</span>{" "}
                            {record.name ?? ""}
                        </h5>
                    </Col>
                    <Col xs={4} className="d-flex justify-content-end gap-2">
                        <Button variant="outline-primary" onClick={() => navigate(-1)}>
                            {tt("pages.thermography.inspectionRecordForm.header.back")}
                        </Button>
                        <Button variant="primary" type="button" onClick={onPrint}>
                            {tt("pages.thermography.inspectionRecordForm.header.viewPrint")}
                        </Button>
                    </Col>
                </Row>
            </div>

            {/* CARD */}
            <div
                className="card p-3"
                style={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                {/* TOP INFO */}
                <div style={{ flex: "0 0 auto" }}>
                    <Row className="mb-3 gx-4">
                        <Form.Group as={Col} md={2}>
                            <Form.Label className="fw-semibold small text-muted">
                                {tt("pages.thermography.inspectionRecordForm.fields.installation")}
                            </Form.Label>
                            <div className="fw-bold">{record.plant?.name ?? "--"}</div>
                        </Form.Group>

                        <Form.Group as={Col} md={2}>
                            <Form.Label className="fw-semibold small text-muted">
                                {tt("pages.thermography.inspectionRecordForm.fields.expectedEnd")}
                            </Form.Label>
                            <div className="fw-bold">
                                {formatDateTime(record.expectedEndDate)}
                            </div>
                        </Form.Group>

                        <Form.Group as={Col} md={2}>
                            <Form.Label className="fw-semibold small">
                                {tt("pages.thermography.inspectionRecordForm.fields.maintenanceDocument")}
                            </Form.Label>
                            <Form.Control
                                type="text"
                                value={record.maintenanceDocument ?? ""}
                                disabled={isViewMode}
                                onChange={(e) => dispatch(setMaintenanceDocument(e.target.value))}
                                onBlur={handleUpdateOnBlur}
                            />
                        </Form.Group>

                        <Form.Group as={Col} md={6}>
                            <Form.Label className="fw-semibold small">
                                {tt("pages.thermography.inspectionRecordForm.fields.recordDescription")}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={1}
                                value={record.description ?? ""}
                                disabled={isViewMode}
                                onChange={(e) => dispatch(setDescription(e.target.value))}
                                onBlur={handleUpdateOnBlur}
                            />
                        </Form.Group>
                    </Row>

                    <div className="d-flex justify-content-between align-items-center mt-2">
                        {hasStarted || hasFinished ? (
                            <div className="small text-muted d-flex flex-wrap align-items-center gap-3">
                                {hasStarted && (
                                    <div className="d-flex align-items-center gap-1">
                                        <span className="fw-semibold text-dark">
                                            {tt("pages.thermography.inspectionRecordForm.info.startedAt")}:
                                        </span>
                                        <span>{formatDateTime(record.startedAt)}</span>
                                        <span className="text-dark">{tt("pages.thermography.inspectionRecordForm.info.by")}</span>
                                        <span>{startedByName}</span>
                                    </div>
                                )}
                                {hasFinished && (
                                    <div className="d-flex align-items-center gap-1">
                                        <span className="fw-semibold text-dark">
                                            {tt("pages.thermography.inspectionRecordForm.info.finishedAt")}:
                                        </span>
                                        <span>{formatDateTime(record.finishedAt)}</span>
                                        <span className="text-dark">{tt("pages.thermography.inspectionRecordForm.info.by")}</span>
                                        <span>{finishedByName}</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div />
                        )}

                        {!hasStarted && (
                            <Button
                                className="ms-auto"
                                variant="success"
                                size="sm"
                                disabled={isViewMode}
                                onClick={handleStartInspection}
                            >
                                {tt("pages.thermography.inspectionRecordForm.actions.startInspection")}
                            </Button>
                        )}
                    </div>
                </div>

                {/* FRAME FLEXÍVEL */}
                <div
                    style={{
                        flex: "1 1 auto",
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        marginTop: 5,
                    }}
                >
                    <div style={{ flex: "1 1 auto", minHeight: 0, display: "flex", gap: 12 }}>
                        {/* FILTROS */}
                        <div style={{ width: 320, minWidth: 280, maxWidth: 380, minHeight: 0 }}>
                            <div
                                style={{
                                    border: "1px solid var(--phoenix-border-color)",
                                    borderRadius: 8,
                                    padding: 12,
                                    height: "60dvh",
                                    overflow: "auto",
                                    background: "var(--phoenix-body-bg, #fff)",
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <div className="fw-bold" style={{ fontSize: 13 }}>
                                        {tt("pages.thermography.inspectionRecordForm.filters.title")}
                                    </div>

                                    {hasAnyFilter ? (
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={clearAllFilters}
                                            style={{ borderRadius: 999, padding: "2px 10px" }}
                                        >
                                            {tt("pages.thermography.inspectionRecordForm.filters.clear")}
                                        </Button>
                                    ) : null}
                                </div>

                                {/* STATUS */}
                                <Form.Label className="fw-semibold small text-muted mb-1">
                                    {tt("pages.thermography.inspectionRecordForm.filters.status")}
                                </Form.Label>
                                <ReactSelect
                                    styles={selectStyles}
                                    options={statusOptions}
                                    value={selectedStatusOptions}
                                    isMulti
                                    closeMenuOnSelect={false}
                                    placeholder={tt("pages.thermography.inspectionRecordForm.filters.selectStatus")}
                                    onChange={onChangeStatuses}
                                    components={{ ClearIndicator }}
                                />

                                <div style={{ borderTop: "1px solid var(--phoenix-border-color)", margin: "12px 0" }} />

                                {/* CONDIÇÃO */}
                                <Form.Label className="fw-semibold small text-muted mb-1">
                                    {tt("pages.thermography.inspectionRecordForm.filters.condition")}
                                </Form.Label>
                                <ReactSelect
                                    styles={selectStyles}
                                    options={condOptions}
                                    value={selectedCondOptions}
                                    isMulti
                                    closeMenuOnSelect={false}
                                    placeholder={tt("pages.thermography.inspectionRecordForm.filters.selectConditions")}
                                    onChange={onChangeConditions}
                                    components={{ ClearIndicator }}
                                />
                            </div>
                        </div>

                        {/* ÁRVORE */}
                        <div style={{ flex: 1, minHeight: 0 }}>
                            <div
                                style={{
                                    height: "60dvh",
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                    padding: "8px 4px",
                                    border: "1px solid var(--phoenix-border-color)",
                                    borderRadius: 6,
                                }}
                            >
                                <InspectionRouteTree
                                    initialData={nodes}
                                    onChange={(newTree) => {
                                        if (isInteractionDisabled) return;
                                        dispatch(setNodes(newTree));
                                    }}
                                    options={{ drag: false, checkbox: false }}
                                    rowStyle={(node) => {
                                        const idStr = String((node as any)?.id ?? "");
                                        const visible = visibilityMap.get(idStr) ?? true;
                                        if (!visible) return { display: "none" };
                                        return rowStyle ? rowStyle(node as any) : undefined;
                                    }}
                                    renderNodeExtras={(node, update) =>
                                        handleRenderNodeExtras(node, update, isInteractionDisabled, handleStatusChange, handleFinishGroup)
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    {/* FOOTER DO CARD */}
                    <div style={{ flex: "0 0 auto", marginTop: 10 }}>
                        <div className="d-flex justify-content-end">
                            {!hasFinished && (
                                <Button
                                    size="sm"
                                    variant="primary"
                                    disabled={isInteractionDisabled}
                                    onClick={() => setShowFinishModal(true)}
                                >
                                    {tt("pages.thermography.inspectionRecordForm.actions.finishAllInspection")}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <PhoenixModal
                show={showFinishModal}
                onHide={() => setShowFinishModal(false)}
                title={tt("pages.thermography.inspectionRecordForm.modal.finishTitle")}
                size="fullscreen-sm-down"
                backdrop="static"
                footer={
                    <>
                        <Button variant="phoenix-secondary" onClick={() => setShowFinishModal(false)}>
                            {tt("pages.thermography.inspectionRecordForm.modal.cancel")}
                        </Button>

                        <Button
                            variant="primary"
                            onClick={() => {
                                handleFinishInspection();
                                setShowFinishModal(false);
                            }}
                        >
                            {tt("pages.thermography.inspectionRecordForm.modal.confirm")}
                        </Button>
                    </>
                }
            >
                <p className="mb-3">{tt("pages.thermography.inspectionRecordForm.modal.description")}</p>
                <div style={{ maxHeight: 240, overflowY: "auto" }}>
                    {groupNames.length ? (
                        <ul className="mb-0" style={{ paddingLeft: 18 }}>
                            {groupNames.map((name, idx) => (
                                <li key={`${name}-${idx}`} className="mb-1">
                                    {name}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-muted small">{tt("pages.thermography.inspectionRecordForm.modal.noGroups")}</div>
                    )}
                </div>
            </PhoenixModal>
        </div>
    );
}
